import { useState } from "react";
import { Plus, Search } from "lucide-react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type { PartialSkillTemplate } from "~/components/assistant/types";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { useDebounce } from "~/hooks/useDebounce";
import { cn } from "~/lib/utils";
import { useSkillTemplates } from "~/pages/datasets/components/assistant/hooks/useSkillTemplates";

interface SkillPickerFormProps {
  api: AssistantApi;
  open: boolean;
  onAddNew: () => void;
  onSelectTemplate: (template: PartialSkillTemplate) => void;
}

const SkillPickerForm = ({ api, open, onAddNew, onSelectTemplate }: SkillPickerFormProps) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const { templates, isLoading } = useSkillTemplates(api, debouncedSearch || undefined, open);

  return (
    <>
      <div className="px-6 pb-4 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search skill templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="px-6 pb-6 overflow-y-auto flex-1">
        {!isLoading && templates.length === 0 ? (
          <div className="flex flex-1 items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">
              No templates match your search. You can still{" "}
              <button
                type="button"
                onClick={onAddNew}
                className="text-primary underline underline-offset-2 hover:text-primary/80"
              >
                create a skill from scratch
              </button>
              .
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onAddNew}
              className={cn(
                "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6",
                "text-muted-foreground hover:border-primary hover:text-primary",
                "transition-colors cursor-pointer"
              )}
            >
              <Plus className="size-5" />
              <span className="text-sm font-medium">Add New Skill</span>
            </button>

            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="rounded-lg border p-4">
                    <Skeleton className="h-4 w-2/3 mb-2" />
                    <Skeleton className="h-3 w-full mb-1" />
                    <Skeleton className="h-3 w-4/5" />
                  </div>
                ))
              : templates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => onSelectTemplate(template)}
                    className={cn(
                      "flex flex-col items-start gap-1 rounded-lg border p-4 text-left",
                      "hover:border-primary hover:bg-accent",
                      "transition-colors cursor-pointer"
                    )}
                  >
                    <span className="text-sm font-medium truncate w-full">{template.name}</span>
                    <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">
                      {template.description}
                    </p>
                  </button>
                ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SkillPickerForm;
